import { reactive } from 'vue'
import { storeToRefs } from 'pinia'
import Jet, { trackDynamic } from '@/modules/Jet'
import { JET_SCREEN_EVENT_NAMES } from '@/composables/jetScreenEventNames'
import { featureFlags as localFeatureFlags } from '@/modules/Onboarding/runtime/external/featureFlags'
import { useUserFlagsStore } from '@/store/userFlags'
import {
	STEP_TYPE,
	POPUP_CLOSE_TYPE,
	formatTitle,
	jetGenerateStepId,
	jetComputeSecondsOnScreen,
	persistStepState,
	readPersistedStepState,
} from '@/composables/stepEventsCore'

const QUESTION_COMPONENT_TYPES = new Set([
	'listInput',
	'checkbox',
	'textInput',
	'dateInput',
	'numberInput',
	'imageInput',
	'rateInput',
	'voteInput',
	'tagsInput',
])

// Canonical button names for OnboardingButtonClick event
const BUTTON_NAME = {
	PRIVACY_POLICY: 'privacy_policy',
	TERMS_OF_USE: 'terms_of_use',
	LANGUAGE_SELECTOR: 'language_selector',
	SYSTEM_OF_MEASUREMENT: 'system_of_measurement',
}

// Event names emitted by custom components via $emit('event-triggered', { name })
const CUSTOM_EVENT_NAME = {
	HEIGHT_FORM_ERROR: 'HeightFormError',
}

// href pattern → button_name for DOM-delegated OnboardingButtonClick tracking.
// Only links whose href matches one of these patterns trigger the event.
const LINK_HREF_TO_BUTTON = [
	{ pattern: /\/privacy/, name: BUTTON_NAME.PRIVACY_POLICY },
	{ pattern: /\/tos\b|\/terms/, name: BUTTON_NAME.TERMS_OF_USE },
]

// Screen-specific Jet event names (from PR #2, request_id=821).
// List lives in jetScreenEventNames.ts with a `satisfies EventName[]` compile-time guard.
// Events in this set are forwarded to Jet via jetScreenSpecificEvent().
const JET_SCREEN_EVENTS = new Set(JET_SCREEN_EVENT_NAMES)

// Property-level type conversions for screen-specific events.
// Properties not listed here are passed as-is (strings, booleans).
const JET_PROPERTY_TYPE = {
	height: 'decimal',
	weight: 'decimal',
	year: 'long',
	secondsOnScreen: 'long',
	date: 'timestamp',
}

// Legacy CMS analyticsKey → Jet screen-specific event name + value property key.
// The renderer fires these via onHandleComponentEvents (onUnmounted) with { data: componentValue }.
// handleCustomAnalyticEvent in OnboardingView uses this map to forward data to jetScreenSpecificEvent.
const LEGACY_TO_JET_EVENT = {
	OnboardingHeightScreenDone: { jetName: 'OnboardingHeightSelectionDone', valueKey: 'height' },
	OnboardingWeightScreenDone: { jetName: 'OnboardingWeightSelectionDone', valueKey: 'weight' },
	OnboardingWeightGoalScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
	OnboardingHappyWeightScreenDone: { jetName: 'OnboardingGoalWeightSelectionDone', valueKey: 'weight' },
}

export {
	STEP_TYPE,
	BUTTON_NAME,
	POPUP_CLOSE_TYPE,
	JET_SCREEN_EVENTS,
	LEGACY_TO_JET_EVENT,
	CUSTOM_EVENT_NAME,
	jetGenerateStepId,
	jetComputeSecondsOnScreen,
}

// ─── Pure helpers (no state/store dependency) ────────────────────────────────

function jetIsConditional(screen) {
	const conditions = screen?.navigationParams?.conditionsForShow ?? []
	return conditions.some((c) => !(c.variableName === 'featureKey' && /^web_/.test(c.value)))
}

function jetComputeStepType(screen) {
	if (screen?.stepType != null) return screen.stepType

	const allComponents = [...(screen?.components ?? []), ...Object.values(screen?.bottomBlock ?? {})]
	const ids = allComponents.map((c) => c.id)

	if (ids.some((id) => QUESTION_COMPONENT_TYPES.has(id))) return STEP_TYPE.QUESTION
	if (ids.includes('button')) return STEP_TYPE.CONFIRM
	if (allComponents.some((c) => c.id === 'custom' && /loader/i.test(c.name))) return STEP_TYPE.LOADER

	return STEP_TYPE.OTHER
}

function jetResolveButtonNameFromHref(href) {
	if (!href) return null
	for (const { pattern, name } of LINK_HREF_TO_BUTTON) {
		if (pattern.test(href)) return name
	}
	return null
}

// ─── Composable ──────────────────────────────────────────────────────────────

export function useOnboardingStepEvents() {
	const { getFlags: getUserFlags } = storeToRefs(useUserFlagsStore())

	const lastStep = readPersistedStepState()
	const stepEvents = reactive({
		currentStepId: lastStep?.stepId || null,
		previousStepId: null,
		currentTitle: lastStep?.title || '',
		previousTitle: '',
		showTimestamp: null,
		isBackwards: false,
		isConditional: false,
		isControl: true,
		isVariation: false,
		stepType: STEP_TYPE.OTHER,
		lastAnswerData: null,
		lastSecondsOnScreen: null,
		_popupStepId: null,
		_popupTitle: null,
	})

	// ─── Store-dependent helpers ─────────────────────────────────────────

	function jetIsAbsmartlyCondition(value) {
		if (value in localFeatureFlags) return false
		if (value === getUserFlags.value?.local?.funnelFeatureFlag) return false
		return true
	}

	function jetIsControl(screen) {
		const conditions = screen?.navigationParams?.conditionsForShow ?? []
		return !conditions.some((c) => c.variableName === 'featureKey' && jetIsAbsmartlyCondition(c.value))
	}

	// ─── Public API ─────────────────────────────────────────────────────

	function jetStepShow(screen, isBackwards, isVariation) {
		const title = formatTitle(screen?.name || '')
		const ts = Date.now()

		const isConditional = jetIsConditional(screen)
		const isControl = jetIsControl(screen)
		const stepType = jetComputeStepType(screen)
		const stepId = jetGenerateStepId(title)

		stepEvents.previousStepId = stepEvents.currentStepId
		stepEvents.previousTitle = stepEvents.currentTitle
		stepEvents.currentStepId = stepId
		stepEvents.currentTitle = title
		stepEvents.showTimestamp = ts
		persistStepState(stepId, title)
		stepEvents.isBackwards = isBackwards
		stepEvents.isConditional = isConditional
		stepEvents.isControl = isControl
		stepEvents.isVariation = !!isVariation
		stepEvents.stepType = stepType
		stepEvents.lastAnswerData = null

		Jet.track('OnboardingStepShow', {
			onboarding_step_id: stepId,
			...(stepEvents.previousStepId && { previous_onboarding_step_id: stepEvents.previousStepId }),
			onboarding_title: title,
			...(stepEvents.previousTitle && { previous_onboarding_title: stepEvents.previousTitle }),
			is_backwards: isBackwards,
			is_control: isControl,
			is_conditional: isConditional,
			is_variation: !!isVariation,
			step_type: stepType,
		})
	}

	function jetStepDone(isStepBack) {
		if (!stepEvents.showTimestamp) return

		const secondsOnScreen = jetComputeSecondsOnScreen(stepEvents.showTimestamp)

		stepEvents.lastSecondsOnScreen = secondsOnScreen

		// Per spec 8.15: when user leaves via back, data_* must be NULL
		const answer = isStepBack ? {} : stepEvents.lastAnswerData || {}

		Jet.track('OnboardingStepDone', {
			onboarding_step_id: stepEvents.currentStepId,
			...(stepEvents.previousStepId && { previous_onboarding_step_id: stepEvents.previousStepId }),
			onboarding_title: stepEvents.currentTitle,
			...(stepEvents.previousTitle && { previous_onboarding_title: stepEvents.previousTitle }),
			is_backwards: stepEvents.isBackwards,
			is_control: stepEvents.isControl,
			is_conditional: stepEvents.isConditional,
			is_variation: stepEvents.isVariation,
			step_type: stepEvents.stepType,
			...(secondsOnScreen !== null && { seconds_on_screen: secondsOnScreen }),
			is_step_back: isStepBack,
			...(answer.data_string != null && { data_string: answer.data_string }),
			...(answer.data_float != null && { data_float: String(answer.data_float) }),
			...(answer.data_int != null && { data_int: answer.data_int }),
			data_array: answer.data_array ?? [],
		})
	}

	function jetPopupShow(modal) {
		// Snapshot stepId/title — the renderer fires screen-changed (next screen)
		// before modal-closed, which would overwrite currentStepId.
		stepEvents._popupStepId = stepEvents.currentStepId
		stepEvents._popupTitle = stepEvents.currentTitle

		Jet.track('OnboardingPopupShow', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(modal?.name && { popup_title: modal.name }),
		})
	}

	function jetPopupDone(modal, closeType = POPUP_CLOSE_TYPE.OTHER) {
		const stepId = stepEvents._popupStepId || stepEvents.currentStepId
		const title = stepEvents._popupTitle || stepEvents.currentTitle

		Jet.track('OnboardingPopupDone', {
			...(stepId && { onboarding_step_id: stepId }),
			...(title && { onboarding_title: title }),
			...(modal?.name && { popup_title: modal.name }),
			close_type: closeType,
		})

		stepEvents._popupStepId = null
		stepEvents._popupTitle = null
	}

	function jetButtonClick(buttonName) {
		Jet.track('OnboardingButtonClick', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(buttonName && { button_name: buttonName }),
		})
	}

	function jetHandleDelegatedClick(event) {
		const el = event.target.closest('a')
		if (!el) return

		const buttonName = jetResolveButtonNameFromHref(el.href)
		if (buttonName) {
			jetButtonClick(buttonName)
		}
	}

	function jetAlertDone(alertDescription, data) {
		Jet.track('OnboardingAlertDone', {
			...(stepEvents.currentStepId && { onboarding_step_id: stepEvents.currentStepId }),
			...(stepEvents.currentTitle && { onboarding_title: stepEvents.currentTitle }),
			...(alertDescription && { alert_description: alertDescription }),
			...(data != null && { data }),
		})
	}

	/**
	 * Forwards a renderer-emitted screen-specific event to Jet.
	 * Adds onboardingStepId automatically. onboardingTitle is NOT added —
	 * only a few events have it in their schema (e.g. OnboardingHealthProblemRiskDone).
	 * Callers must pass onboardingTitle explicitly in properties when needed.
	 * Unknown events (not in JET_SCREEN_EVENTS) are silently ignored.
	 */
	function jetScreenSpecificEvent(eventName, properties = {}) {
		if (!JET_SCREEN_EVENTS.has(eventName)) return

		const converted = {}
		if (stepEvents.currentStepId) {
			converted.onboardingStepId = stepEvents.currentStepId
		}

		for (const [key, value] of Object.entries(properties || {})) {
			if (key === 'data') continue // raw answer — already captured by jetCaptureAnswer
			if (value == null) continue

			const type = JET_PROPERTY_TYPE[key]
			if (type === 'decimal') {
				// Keep a NUMBER (not String): the decimal wire field is a protobuf `string`, but the SDK's
				// BinaryWriter.string() coerces number→string byte-identically (`.string(150)` === `.string("150")`),
				// so the wire stays unchanged while the dev-console log shows a number — matching the old Jet.js
				// #logger, which normalised the Decimal to a number for display (Decimal.toNumber()).
				converted[key] = Number(value)
			} else if (type === 'long') {
				converted[key] = value
			} else if (type === 'timestamp') {
				converted[key] = new Date(value).getTime()
			} else {
				converted[key] = value
			}
		}

		// OnboardingWelcomeDone needs secondsOnScreen from the StepDone that just fired.
		// Timing: renderer emits @screen-changed (→ jetStepDone stores lastSecondsOnScreen)
		// before @event-triggered (→ jetScreenSpecificEvent uses it).
		if (eventName === 'OnboardingWelcomeDone' && !converted.secondsOnScreen && stepEvents.lastSecondsOnScreen != null) {
			converted.secondsOnScreen = stepEvents.lastSecondsOnScreen
		}

		// Dynamic event name (from JET_SCREEN_EVENTS) → trackDynamic snakes keys + tracks.
		trackDynamic(eventName, converted)
	}

	// Called from surveyEventHandler when a *ScreenNameDone* event fires
	function jetCaptureAnswer(data) {
		const raw = data?.properties?.data
		if (raw === undefined || raw === null) return

		// Compound data: {data_string: 'cm', data_float: 175}
		if (typeof raw === 'object' && !Array.isArray(raw)) {
			stepEvents.lastAnswerData = raw
			return
		}

		if (Array.isArray(raw)) {
			stepEvents.lastAnswerData = { data_array: [...raw].map(String).sort() }
		} else if (typeof raw === 'number') {
			if (Number.isInteger(raw)) {
				stepEvents.lastAnswerData = { data_int: raw }
			} else {
				stepEvents.lastAnswerData = { data_float: raw }
			}
		} else if (typeof raw === 'string') {
			stepEvents.lastAnswerData = { data_string: raw }
		}
	}

	return {
		stepEvents,
		jetStepShow,
		jetStepDone,
		jetPopupShow,
		jetPopupDone,
		jetButtonClick,
		jetHandleDelegatedClick,
		jetAlertDone,
		jetScreenSpecificEvent,
		jetCaptureAnswer,
	}
}
